import {
  MultilegReportingTypeEnum,
  OrderMarketStatusEnum,
  toBigWithDefault,
  type IOMSExecutionReport4203LegSummary,
  type IOMSExecutionReport4203Markets,
  type MarketAccount,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { hasUnifiedLiquiditySelection, isValidUnifiedLiquidityTokenSecurity } from './utils/unifiedLiquidity';

export const UnifiedLiquidityMarketTooltipStatus = {
  Enabled: 'Enabled',
  Disabled: 'Disabled',
  Unavailable: 'Unavailable',
} as const;
export type UnifiedLiquidityMarketTooltipStatus =
  (typeof UnifiedLiquidityMarketTooltipStatus)[keyof typeof UnifiedLiquidityMarketTooltipStatus];

export interface UnifiedLiquidityMarketTooltipGroup {
  key: string;
  displayName: string;
  market: string;
  rows: UnifiedLiquidityMarketTooltipRow[];
}

export interface UnifiedLiquidityMarketTooltipRow {
  symbol: string;
  displayName: string;
  status?: UnifiedLiquidityMarketTooltipStatus;
}

export function getUnifiedLiquiditySymbols(
  groups: UnifiedLiquidityMarketTooltipGroup[],
  { baseSymbol, enabledOnly = false }: { baseSymbol?: string; enabledOnly?: boolean } = {}
) {
  return Array.from(
    new Set([
      ...(baseSymbol == null ? [] : [baseSymbol]),
      ...groups.flatMap(group =>
        group.rows
          .filter(
            row => !enabledOnly || row.status == null || row.status === UnifiedLiquidityMarketTooltipStatus.Enabled
          )
          .map(row => row.symbol)
      ),
    ])
  );
}

type EntityUnifiedLiquidityMarket = Partial<
  Pick<IOMSExecutionReport4203Markets, 'Market' | 'MarketAccount' | 'MarketStatus' | 'Symbol' | 'CumQty' | 'LegIndex'>
>;

type EntityUnifiedLiquidityLegSummary = Partial<
  Pick<IOMSExecutionReport4203LegSummary, 'LegIndex' | 'MultilegReportingType'>
>;

export function getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets({
  markets,
  legSummary,
  marketAccountsByName,
  marketDisplayNameByName,
  securitiesBySymbol,
}: {
  markets: EntityUnifiedLiquidityMarket[] | IOMSExecutionReport4203Markets[] | undefined;
  legSummary?: EntityUnifiedLiquidityLegSummary[] | IOMSExecutionReport4203LegSummary[];
  marketAccountsByName: Map<string, MarketAccount>;
  marketDisplayNameByName: Map<string, string>;
  securitiesBySymbol: Map<string, Security>;
}): UnifiedLiquidityMarketTooltipGroup[] {
  const groupsByKey = new Map<string, UnifiedLiquidityMarketTooltipGroup>();
  const symbolsByGroupKey = new Map<string, Set<string>>();

  for (const market of markets ?? []) {
    if (!shouldIncludeEntityMarket(market, legSummary)) {
      continue;
    }

    const symbol = market.Symbol;
    if (!symbol) {
      continue;
    }

    const groupKey = market.MarketAccount ?? market.Market;
    if (!groupKey) {
      continue;
    }

    const group = groupsByKey.get(groupKey) ?? {
      key: groupKey,
      displayName: getEntityMarketGroupDisplayName({
        groupKey,
        marketAccount: market.MarketAccount,
        market: market.Market,
        marketAccountsByName,
        marketDisplayNameByName,
      }),
      market: market.Market ?? groupKey,
      rows: [],
    };

    const symbols = symbolsByGroupKey.get(groupKey) ?? new Set<string>();
    if (!symbols.has(symbol)) {
      symbols.add(symbol);
      group.rows.push({
        symbol,
        displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol,
      });
    }

    symbolsByGroupKey.set(groupKey, symbols);
    groupsByKey.set(groupKey, group);
  }

  return Array.from(groupsByKey.values())
    .map(group => ({
      ...group,
      rows: group.rows.toSorted((a, b) => a.displayName.localeCompare(b.displayName)),
    }))
    .toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

export function getUnifiedLiquidityTooltipGroupsFromTokens(
  tokens: UnifiedLiquidityToken[],
  securitiesBySymbol: ReadonlyMap<string, { DisplaySymbol?: string }>,
  marketDisplayNameByName: ReadonlyMap<string, string>
): UnifiedLiquidityMarketTooltipGroup[] {
  const symbolsByMarket = new Map<string, Set<string>>();
  for (const token of tokens) {
    const symbols = symbolsByMarket.get(token.Market) ?? new Set<string>();
    symbols.add(token.Symbol);
    symbolsByMarket.set(token.Market, symbols);
  }

  return Array.from(symbolsByMarket, ([market, symbols]) => ({
    key: market,
    market,
    displayName: marketDisplayNameByName.get(market) ?? market,
    rows: Array.from(symbols)
      .map(symbol => ({ symbol, displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol }))
      .toSorted((a, b) => a.displayName.localeCompare(b.displayName)),
  })).toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

interface GetUnifiedLiquidityMarketAccountTooltipGroupParams {
  marketAccount: MarketAccount;
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
  selectedUnifiedLiquidityMarketSymbols: { marketAccount: string; symbol: string }[];
  securitiesBySymbol: Map<string, Security>;
}

export function getUnifiedLiquidityMarketAccountTooltipGroup({
  marketAccount,
  primarySymbol,
  primarySecurity,
  unifiedLiquidityTokens,
  selectedUnifiedLiquidityMarketSymbols,
  securitiesBySymbol,
}: GetUnifiedLiquidityMarketAccountTooltipGroupParams): UnifiedLiquidityMarketTooltipGroup {
  const symbolCandidates = getUnifiedLiquiditySymbolCandidatesForMarket({
    market: marketAccount.Market,
    primarySymbol,
    primarySecurity,
    unifiedLiquidityTokens,
    securitiesBySymbol,
  });
  return {
    key: marketAccount.Name,
    displayName: marketAccount.DisplayName ?? marketAccount.Name,
    market: marketAccount.Market,
    rows: symbolCandidates.map(symbol => ({
      symbol,
      displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol,
      status: hasUnifiedLiquiditySelection(selectedUnifiedLiquidityMarketSymbols, {
        marketAccount: marketAccount.Name,
        symbol,
      })
        ? UnifiedLiquidityMarketTooltipStatus.Enabled
        : UnifiedLiquidityMarketTooltipStatus.Disabled,
    })),
  };
}

export function getUnifiedLiquiditySymbolCandidatesForMarket({
  market,
  primarySymbol,
  primarySecurity,
  unifiedLiquidityTokens,
  securitiesBySymbol,
}: {
  market: string;
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
  securitiesBySymbol: Map<string, Security>;
}) {
  return Array.from(
    new Set([
      ...(primarySymbol &&
      primarySecurity?.Markets?.includes(market) &&
      isValidUnifiedLiquidityTokenSecurity(primarySecurity)
        ? [primarySymbol]
        : []),
      ...unifiedLiquidityTokens
        .filter(token => token.Market === market)
        .map(token => token.Symbol)
        .filter(symbol => isValidUnifiedLiquidityTokenSecurity(securitiesBySymbol.get(symbol))),
    ])
  ).toSorted((a, b) => a.localeCompare(b));
}

function shouldIncludeEntityMarket(
  market: EntityUnifiedLiquidityMarket,
  legSummary?: EntityUnifiedLiquidityLegSummary[] | IOMSExecutionReport4203LegSummary[]
) {
  const leg = legSummary?.find(summary => summary.LegIndex === market.LegIndex);
  if (leg && leg.MultilegReportingType !== MultilegReportingTypeEnum.Parent) {
    return false;
  }

  return market.MarketStatus !== OrderMarketStatusEnum.Disabled || toBigWithDefault(market.CumQty, 0).gt(0);
}

function getEntityMarketGroupDisplayName({
  groupKey,
  marketAccount,
  market,
  marketAccountsByName,
  marketDisplayNameByName,
}: {
  groupKey: string;
  marketAccount: string | undefined;
  market: string | undefined;
  marketAccountsByName: Map<string, MarketAccount>;
  marketDisplayNameByName: Map<string, string>;
}) {
  if (marketAccount) {
    return marketAccountsByName.get(marketAccount)?.DisplayName ?? marketAccount;
  }

  return market ? (marketDisplayNameByName.get(market) ?? market) : groupKey;
}
